import {Cart, CartLineItem, LineItemInput} from '@/cart/types';
import {
  DeliveryItem,
  DeliveryOption,
  GiftProduct,
  getDefaultDeliveryOption,
} from '@/product/delivery-option';
import {
  HasSelectedOptions,
  ProductSelectSectionSettings,
  VariantImageOverride,
} from '@/settings/types';
import {
  Image,
  Product,
  ProductOption,
  ProductVariant,
  ProductVariantOption,
} from '@/product/types';
import {Signal, batch, effect, signal} from '@preact/signals';

import {StepStrategy} from '@/product/step-strategy';
import {logError} from '@/utilities/log';
import parseId from '@/utilities/parse-id';
import {getDonationCategory} from '@/cart/cart-store';

/**
 * An implementation of [ProductSelectController] using Preact Signals to manage state.
 * This class is responsible for managing the state of the [ProductSelectSection].
 * It depends on the [ProductStore] and [CartStore] to retrieve product and cart data.
 */
export default class ProductSelectControllerSignal {
  public readonly deliveryOptions: DeliveryOption[];

  private readonly _cart: CartStore;
  private readonly _delivery: Signal<DeliveryOption | null> = signal(null);
  private readonly _imageMapping?: ProductSelectImageMapping;
  private readonly _optionMap: Signal<Map<string, string>> = signal(
    new Map<string, string>()
  );
  private readonly _product: Signal<Product | null> = signal(null);
  private readonly _step: Signal<number> = signal(1);
  private _stepStrategy: StepStrategy;
  private readonly _settings: ProductSelectSectionSettings;

  constructor({
    cart,
    deliveryOptions,
    product,
    productId,
    products,
    settings,
  }: ProductSelectControllerSignalOptions) {
    const deliveryFirst = settings.step_order === 'delivery_first';
    const ignoreOptions = settings.ignore_options ?? [];
    const imageMapping = {
      featuredImage: settings.featured_image_override,
      variantImages: settings.variant_image_overrides,
    };

    this.deliveryOptions = deliveryOptions;
    this._cart = cart;
    this._imageMapping = imageMapping;
    this._settings = settings;

    effect(() => {
      const delivery = getDefaultDeliveryOption(
        this.deliveryOptions,
        settings.default_delivery_option
      );
      const productData = product ?? products.getProduct(productId);
      const productOptions =
        productData?.options?.filter(
          ({name}) => !ignoreOptions?.includes(name)
        ) ?? [];
      this._stepStrategy = StepStrategy.fromOptions({
        productOptions,
        deliveryOptions: this.deliveryOptions,
        deliveryFirst: deliveryFirst ?? false,
      });
      batch(() => {
        this._delivery.value = delivery;
        this._optionMap.value = mapFromOptions(productOptions);
        this._product.value = productData
          ? {...productData, options: productOptions}
          : null;
      });
    });
  }

  /**
   * The option name for the current step.
   */
  public get currentOptionName(): string {
    const product = this.product;
    const hasProductOptions = product?.options?.length ?? 0 > 0;
    if (!hasProductOptions) return '';
    return this._stepStrategy.getOptionName(this.step);
  }

  /**
   * The image for the current product variant.
   * This is the featured image if no variant is selected.
   */
  public get image(): Image {
    const variantImageOverride = this._imageMapping?.variantImages?.find(
      (entry) => this.matchesEveryOption(entry)
    )?.image;
    const selectedVariantImage = this.getSelectedDeliveryItem()?.image;
    const variantImage = this._stepStrategy.shouldShowVariantImage(this.step)
      ? variantImageOverride ?? selectedVariantImage
      : null;
    const featuredImage =
      this._imageMapping?.featuredImage ?? this.product?.featuredImage;

    return variantImage ?? featuredImage ?? {url: '', alt: ''};
  }

  public get maxGifts(): GiftProduct[] {
    return this.deliveryOptions.reduce(
      (maxGifts, option) =>
        maxGifts.length > option.gifts.length ? maxGifts : option.gifts,
      []
    );
  }

  /**
   * The current product.
   * This is the product with the given ID from the product store.
   * If no product is found, this is null.
   */
  public get product(): Product | null {
    return this._product.value;
  }

  /**
   * The selected delivery option.
   * This is the first delivery option by default.
   */
  public get selectedDeliveryOption(): DeliveryOption | null {
    return this._delivery.value;
  }

  public get selectedGifts(): GiftProduct[] {
    return this.selectedDeliveryOption?.gifts ?? [];
  }

  /**
   * Get the option settings for the selected variant option.
   * Returns the first option settings entry that match the selected options.
   */
  public get selectedOptionSettings() {
    const map = this._settings.product_options_settings_map;
    for (const entry of map) {
      const value = this._optionMap.value.get(entry.name);
      if (entry.value === value) {
        return entry;
      }
    }
  }

  /**
   * The selected variant option.
   * This is the variant that matches the selected options.
   * If no variant matches, this is null.
   * If no options are selected, this is the first variant.
   */
  public get selectedVariantOption(): ProductVariantOption | null {
    const product = this.product;
    if (!product) return null;

    const variant = product.variants.find((variant) =>
      this.matchesEveryOption(variant)
    );

    return variant ? this.createVariantOption(variant) : null;
  }

  /**
   * Whether to show the product option selector or not.
   * This is `true` if the current step is less than or equal to the number of options.
   */
  public get showVariantOptions(): boolean {
    const product = this.product;
    if (!product) return true;
    return this._stepStrategy.shouldShowVariantOptions(this.step);
  }

  /**
   * Whether to show the delivery option selector or not.
   * This becomes `true` once the step is greater than the number of options.
   * Thus delivery selection is always the last step.
   */
  public get showDeliveryOptions(): boolean {
    return !this.showVariantOptions && this.deliveryOptions.length > 0;
  }

  /**
   * The current step in the product selection process.
   */
  public get step(): number {
    return this._step.value;
  }

  /**
   * The total number of steps in the product selection process.
   */
  public get stepsCount(): number {
    const product = this.product;
    const deliveryOptions = this.deliveryOptions;
    if (!product) return 1;

    const extraSteps = deliveryOptions.length > 0 ? 2 : 1;
    const optionSteps = product.options.length;
    return optionSteps + extraSteps;
  }

  /**
   * The variant options for the current step.
   * This allows the customer to select a variant option for the current step.
   */
  public get variantOptions(): ProductVariantOption[] {
    const product = this.product;
    if (!product) return [];

    const optionName = this.currentOptionName;
    const option = product.options.find(({name}) => name === optionName);
    if (!option) return [];

    const variantOptions: ProductVariantOption[] = [];
    for (const value of option.values) {
      const optionMap = new Map([
        ...this._optionMap.value,
        [optionName, value],
      ]);
      const variant = this.variantFromOptions(optionMap);
      if (!variant) continue;

      const variantOption = this.createVariantOption(variant);
      if (variantOption) variantOptions.push(variantOption);
    }

    return variantOptions;
  }

  /**
   * Move to the next step in the product selection process.
   * If the current step is the last step, this adds the product to the cart.
   */
  public async nextStep(): Promise<void> {
    const isLastStep = this.step === this.stepsCount - 1;
    if (isLastStep) {
      try {
        await this.addToCart();
      } catch (error) {
        logError(error);
      }
    } else {
      this._step.value++;
    }
  }

  /**
   * Move to the previous step in the product selection process.
   * If the current step is the first step, this does nothing.
   */
  public previousStep(): void {
    if (this.step === 1) return;
    this._step.value--;
  }

  /**
   * Select a delivery option.
   * This is the delivery option that will be added to the cart.
   */
  public selectDeliveryOption(option: DeliveryOption): void {
    this._delivery.value = option;
  }

  /**
   * Select a variant option for the current step.
   * This is the option that will be used to select the variant.
   */
  public selectVariantOption({
    optionName,
    optionValue,
  }: ProductVariantOption): void {
    const newOptionMap = new Map(this._optionMap.value);
    newOptionMap.set(optionName, optionValue);
    this._optionMap.value = newOptionMap;
  }

  /**
   * Add the selected product to the cart.
   * This adds the main item and all gift items to the cart.
   * If gift items are already in the cart, their properties are updated to include the main item ID.
   * This allows us to track which items are part of a gift bundle.
   */
  private async addToCart(): Promise<void> {
    const mainItem = this.createMainItem();
    if (!mainItem) return;

    const cart = await this._cart.loadCart();
    const giftItems = this.createGiftItems(cart?.items ?? []);

    // add the main item last so it will be displayed first in the cart
    await this._cart.addLineItems([...giftItems, mainItem]).catch(logError);
  }

  /**
   * Create a [LineItemInput] for the main item.
   * This is the item that will be added to the cart.
   * If the item has a selling plan, the selling plan ID and shipping details
   * are added to the line item properties.
   *
   * If no item matches the current options, this is null.
   */
  private createMainItem() {
    const item = this.getSelectedDeliveryItem() ?? this.variantFromOptions();
    if (!item) return;

    const giftItemIds = this.selectedGifts.map(({variants}) =>
      parseId(variants[0].id)
    );

    const plan = this.selectedDeliveryOption?.sellingPlan;

    // Properties with empty value should be removed by `createLineItemInput`
    const properties = {
      gift_item_ids: giftItemIds.length > 0 ? JSON.stringify(giftItemIds) : '',
      _buy_one_give_ten_category: getDonationCategory() ?? '',
    };

    const planId = plan?.id ? parseId(plan.id) : undefined;

    return this.createLineItemInput({
      id: parseId(item.id),
      properties,
      selling_plan: planId,
    });
  }

  /**
   * Create a [LineItemInput] for a product variant.
   * All params except `id` are optional.
   * If an empty or `undefined` value is given, it is not included in the input.
   * Quantity is 1 by default.
   */
  private createLineItemInput({
    id,
    quantity,
    properties,
    selling_plan,
  }: {id: number} & Partial<LineItemInput>): LineItemInput {
    const item: LineItemInput = {
      id,
      quantity: 1,
    };

    if (quantity) item.quantity = quantity;

    const propertyEntries = Object.entries(properties ?? {}).filter(
      ([, value]) => value
    );
    if (propertyEntries.length > 0) {
      item.properties = Object.fromEntries(propertyEntries);
    }

    if (selling_plan) item.selling_plan = selling_plan;

    return item;
  }

  /**
   * Create a [LineItemInput] for each gift item.
   * This is used to add the gift items to the cart.
   * If a gift item is already in the cart, it is not included in the input.
   * This prevents duplicate gift items from being added to the cart.
   */
  private createGiftItems(cartItems: CartLineItem[]): LineItemInput[] {
    const giftItems: LineItemInput[] = [];

    for (const gift of this.selectedGifts) {
      const id = parseId(gift.variants[0].id);
      const isInCart = cartItems.some((item) => item.id === id);

      if (isInCart) continue;

      const input = this.createLineItemInput({
        id,
        properties: {is_gift: 'true'},
      });
      giftItems.push(input);
    }

    // Reverse the order so the gifts will display in order in the cart
    return giftItems.reverse();
  }

  /**
   * Get the [DeliveryItem] that matches the currently selected options.
   * This allows us to get the price and other details for the selected item,
   * which are used to add the item to the cart.
   *
   * By default, uses the currently selected delivery option.
   * If a different [DeliveryOption] is passed in, it will use that instead.
   *
   * If no item in the delivery option matches the selected options, this is null.
   *
   * @param option - The delivery option to use. If not given, uses the currently selected delivery option.
   * @returns The selected delivery item, or null if no item matches the selected options.
   *
   * @example Usage with the currently selected delivery option:
   * ```ts
   * const item = controller.getSelectedDeliveryItem();
   * if (item) {
   *  console.log(`Selected item: ${item.title}`);
   * }
   * ```
   *
   * @example Usage with a different delivery option:
   * ```ts
   * const option = controller.deliveryOptions[0];
   * const item = controller.getSelectedDeliveryItem(option);
   * if (item) {
   * console.log(`Selected item: ${item.title}`);
   * }
   * ```
   */
  public getSelectedDeliveryItem(option?: DeliveryOption): DeliveryItem | null {
    const deliveryOption = option ?? this._delivery.value;
    if (!deliveryOption) {
      return null;
    }

    const item = deliveryOption?.items.find((item) =>
      this.matchesEveryOption(item, this._optionMap.value)
    );
    return item ?? null;
  }

  /**
   * Create a [ProductVariantOption] from a [ProductVariant].
   * This is used to create the variant options for the current step.
   * If the variant does not have an image or description, the product's featured image and description are used.
   */
  private createVariantOption({
    image,
    selectedOptions,
  }: ProductVariant): ProductVariantOption | null {
    const option = selectedOptions.find(
      (option) => option.name === this.currentOptionName
    );
    const optionSettings = this.selectedOptionSettings;
    return {
      image: image ?? this.product?.featuredImage ?? {url: '', alt: ''},
      description:
        optionSettings?.description ?? this.product?.description ?? '',
      optionName: option?.name ?? '',
      optionValue: option?.value ?? '',
    };
  }

  /**
   * Whether a [ProductVariant] matches the given options.
   * If no options are given, this checks if the variant matches the current options.
   */
  private matchesEveryOption(
    variant: HasSelectedOptions,
    options?: Map<string, string>
  ) {
    const entries = Array.from((options ?? this._optionMap.value).entries());
    return entries.every(([key, value]) =>
      this.matchesOption(variant, key, value)
    );
  }

  /**
   * Whether a [ProductVariant] matches the given option.
   */
  private matchesOption(
    variant: HasSelectedOptions,
    name: string,
    value: string
  ) {
    return variant.selectedOptions.some(
      (option) => option.name === name && option.value === value
    );
  }

  /**
   * Get the [ProductVariant] that matches the given options.
   * This is used to select the variant for the current step.
   */
  private variantFromOptions(
    options?: Map<string, string>
  ): ProductVariant | null {
    const product = this.product;
    if (!product) return null;

    const variant = product.variants.find((variant) =>
      this.matchesEveryOption(variant, options)
    );

    return variant ?? null;
  }
}

/**
 * Create a [Map] from a list of [ProductOption]s.
 * This is used to initialize the option map.
 */
function mapFromOptions(options: ProductOption[]): Map<string, string> {
  const entries = options.map(
    ({name, values}) => [name, values[0] ?? ''] as [string, string]
  );
  const map = new Map<string, string>(entries);
  return map;
}

export type CartStore = {
  // we don't use the resolved value of this Promise, so we can use unknown
  addLineItems: (items: LineItemInput[]) => Promise<unknown>;
  loadCart: () => Promise<Cart | null>;
};

export type ProductStore = {
  getProduct: (id: string) => Product | null;
};

export type ProductSelectControllerSignalOptions = {
  cart: CartStore;
  deliveryOptions: DeliveryOption[];
  product?: Product | null;
  productId: string;
  products: ProductStore;
  settings: ProductSelectSectionSettings;
};

export type ProductSelectImageMapping = {
  featuredImage?: Image;
  variantImages?: VariantImageOverride[];
};
